import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Target, Trophy, MapPin, Users, TrendingUp, Sparkles, Loader2,
  AlertTriangle, CheckCircle2, Pencil, Lightbulb, Heart,
} from "lucide-react";

const RISK_BADGE = {
  "düşük": "bg-green-100 text-green-800 border-green-200",
  "orta": "bg-amber-100 text-amber-800 border-amber-200",
  "yüksek": "bg-orange-100 text-orange-800 border-orange-200",
  "çok yüksek": "bg-red-100 text-red-800 border-red-200",
};

const SUBJECT_LABEL = {
  matematik: "Matematik", turkce: "Türkçe", fen: "Fen Bilimleri",
  inkilap: "İnkılap", din: "Din Kültürü", ingilizce: "İngilizce",
};

export default function HedefPage() {
  const [progress, setProgress] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const load = () => {
    api.get("/target/progress").then((r) => {
      setProgress(r.data);
      if (!r.data.target) setShowPicker(true);
    });
  };
  useEffect(() => { load(); }, []);

  const loadStrategy = async () => {
    setStrategyLoading(true);
    try {
      const r = await api.get("/target/strategy");
      setStrategy(r.data);
    } catch {
      toast.error("Strateji yüklenemedi");
    } finally {
      setStrategyLoading(false);
    }
  };

  if (!progress) return <div className="text-slate-500">Yükleniyor…</div>;

  if (showPicker || !progress.target) {
    return <SchoolPicker onPicked={() => { setShowPicker(false); load(); }} onCancel={() => setShowPicker(false)} currentKey={progress.target?.school_key} />;
  }

  const t = progress.target;
  const est = progress.estimate;
  const gap = progress.gap;
  const targetNets = progress.target_nets;

  return (
    <div className="space-y-5 ela-fade-in" data-testid="hedef-page">
      {/* Hero */}
      <div className="rounded-2xl bg-slate-900 text-white p-6 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-rose-500/20 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 text-rose-200 text-xs uppercase tracking-wider font-semibold">
            <Target className="w-3.5 h-3.5" /> Hedef Lise
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-1">{t.school_name}</h1>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-300 mt-2">
            <span className="flex items-center gap-1"><Trophy className="w-3 h-3" /> {t.school_type_label}</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.city} / {t.district}</span>
            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> Kontenjan {t.quota}</span>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-5">
            <div>
              <div className="text-[10px] text-slate-400">Geçen yıl taban</div>
              <div className="font-display text-xl sm:text-2xl font-bold">{t.last_score}</div>
              <div className="text-[10px] text-slate-400">%{t.last_percentile} dilim</div>
            </div>
            <div>
              <div className="text-[10px] text-rose-200">Güvenli hedef</div>
              <div className="font-display text-xl sm:text-2xl font-bold text-rose-300">{t.safe_score}</div>
              <div className="text-[10px] text-rose-200">%{t.safe_percentile} dilim</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400">Tahmini puanın</div>
              <div className="font-display text-xl sm:text-2xl font-bold">{est.estimated_score}</div>
              <div className="text-[10px] text-slate-400">{est.total_net} net</div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            {gap && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`${RISK_BADGE[gap.risk_level] || ""} border`}>
                  Risk: {gap.risk_level}
                </Badge>
                <span className="text-sm text-slate-200">
                  {gap.gap_points > 0
                    ? <>Kapanması gereken fark: <b className="text-white">{gap.gap_points} puan</b></>
                    : <>Hedefi tutuyorsun 🎉</>}
                </span>
              </div>
            )}
            <Button variant="outline" size="sm"
              onClick={() => setShowPicker(true)}
              data-testid="change-target-btn"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 rounded-xl">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Hedefi Değiştir
            </Button>
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-500 italic">
        Tahminler yaklaşıktır; gerçek LGS puanı standart puana göre hesaplanır. Taban puanlar her yıl değişebilir.
      </p>

      <Tabs defaultValue="net" className="space-y-4">
        <TabsList className="bg-slate-100 rounded-xl">
          <TabsTrigger value="net" data-testid="tab-net">Net Hedefi</TabsTrigger>
          <TabsTrigger value="strateji" data-testid="tab-strateji">AI Strateji</TabsTrigger>
        </TabsList>

        <TabsContent value="net" className="space-y-3">
          <Card className="p-5 rounded-2xl border-slate-200">
            <h3 className="font-heading font-semibold text-slate-900 mb-1">Yaklaşık Hedef Net Planı</h3>
            <p className="text-xs text-slate-500 mb-4">
              Bu okul türü için ders ağırlığına göre dağıtılmış yaklaşık net hedefleri. Kesin sonuç değildir.
            </p>
            <div className="space-y-3">
              {Object.entries(targetNets.subject_target_nets).map(([key, target_n]) => {
                const total_q = targetNets.subject_question_counts[key];
                const current_n = est.subject_nets[key];
                const pct = (current_n / target_n) * 100;
                const reached = current_n >= target_n;
                return (
                  <div key={key} data-testid={`net-row-${key}`}>
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="text-sm font-medium text-slate-700">{SUBJECT_LABEL[key]}</span>
                      <span className="text-xs text-slate-500">
                        <span className={reached ? "text-green-600 font-semibold" : "text-slate-900 font-semibold"}>
                          {current_n}
                        </span>
                        <span className="text-slate-400"> / </span>
                        <span className="text-[#FF6B6B] font-semibold">{target_n}</span>
                        <span className="text-slate-400"> net</span>
                        <span className="text-slate-400 ml-2">/ {total_q} soru</span>
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden relative">
                      <div className={`h-full ${reached ? "bg-green-500" : "bg-[#FF6B6B]"}`}
                        style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between text-sm">
              <span className="text-slate-500">Toplam hedef net</span>
              <span className="font-display font-bold text-slate-900">
                {est.total_net} <span className="text-slate-400 font-normal text-xs">/ {targetNets.target_total_net}</span>
              </span>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="strateji">
          {!strategy ? (
            <Card className="p-8 rounded-2xl border-slate-200 text-center">
              <Sparkles className="w-10 h-10 text-[#FF6B6B] mx-auto mb-3" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">AI Strateji Önerisi</h3>
              <p className="text-sm text-slate-500 mt-1 mb-5">Hedefe yaklaşmak için sana özel yorum + bu hafta odak.</p>
              <Button onClick={loadStrategy} disabled={strategyLoading}
                data-testid="load-strategy-btn"
                className="bg-[#FF6B6B] hover:bg-[#FF5252] rounded-xl">
                {strategyLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Hazırlanıyor…</> : "Stratejiyi Üret"}
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              <Card className="p-5 rounded-2xl border-slate-200 bg-rose-50/40">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#FF6B6B]" />
                  <h3 className="font-heading font-semibold">Strateji Özeti</h3>
                </div>
                <p className="text-sm text-slate-700">{strategy.strateji_ozeti}</p>
              </Card>
              <Card className="p-5 rounded-2xl border-slate-200">
                <h3 className="font-heading font-semibold mb-2">Bu Hafta Odak</h3>
                <ul className="space-y-2">
                  {(strategy.bu_hafta_odak || []).map((o, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-700">
                      <Target className="w-4 h-4 text-[#FF6B6B] mt-0.5 shrink-0" /> {o}
                    </li>
                  ))}
                </ul>
              </Card>
              {strategy.dikkat_noktasi && (
                <Card className="p-4 rounded-2xl border-amber-200 bg-amber-50/50">
                  <div className="flex gap-2 items-start">
                    <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5" />
                    <p className="text-sm text-amber-900">{strategy.dikkat_noktasi}</p>
                  </div>
                </Card>
              )}
              {strategy.motivasyon && (
                <Card className="p-5 rounded-2xl bg-slate-900 text-white">
                  <div className="flex items-center gap-2 text-rose-200 text-xs mb-1">
                    <Heart className="w-3 h-3" /> Motivasyon
                  </div>
                  <p className="text-sm">{strategy.motivasyon}</p>
                </Card>
              )}
              <Button variant="outline" onClick={loadStrategy} className="rounded-xl w-full"
                data-testid="refresh-strategy-btn">
                Stratejiyi Yenile
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SchoolPicker({ onPicked, onCancel, currentKey }) {
  const [schools, setSchools] = useState([]);
  const [typeLabels, setTypeLabels] = useState({});
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(currentKey || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/target/schools").then((r) => {
      setSchools(r.data.schools || []);
      setTypeLabels(r.data.type_labels || {});
    });
  }, []);

  const visible = filter === "all" ? schools : schools.filter((s) => s.type === filter);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post("/target/me", { school_key: selected });
      toast.success("Hedef belirlendi 🎯");
      onPicked();
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 ela-fade-in" data-testid="school-picker">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Hedef Liseni Seç</h1>
        <p className="text-sm text-slate-500 mt-1">Bu hedef, çalışma planını ve net hedeflerini şekillendirecek.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">Tümü</FilterChip>
        {Object.entries(typeLabels).map(([k, label]) => (
          <FilterChip key={k} active={filter === k} onClick={() => setFilter(k)} testid={`filter-${k}`}>{label}</FilterChip>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {visible.map((s) => {
          const isSel = selected === s.key;
          return (
            <button key={s.key} onClick={() => setSelected(s.key)}
              data-testid={`school-${s.key}`}
              className={`text-left p-4 rounded-2xl border transition ${
                isSel ? "border-[#FF6B6B] bg-rose-50/60 ring-2 ring-rose-100" : "border-slate-200 hover:border-slate-300 bg-white"
              }`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-slate-900 truncate">{s.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.type_label} · {s.city}/{s.district}</div>
                </div>
                {isSel && <CheckCircle2 className="w-5 h-5 text-[#FF6B6B] shrink-0" />}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-[10px] text-slate-500">Son Taban</div>
                  <div className="font-display font-bold text-slate-900">{s.last_score}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500">Yüzdelik</div>
                  <div className="font-display font-bold text-slate-900">%{s.last_percentile}</div>
                </div>
                <div>
                  <div className="text-[10px] text-rose-600">Güvenli</div>
                  <div className="font-display font-bold text-[#FF6B6B]">{s.safe_score}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex gap-2 sticky bottom-20 md:bottom-4">
        {currentKey && (
          <Button variant="outline" onClick={onCancel} className="rounded-xl"
            data-testid="picker-cancel">İptal</Button>
        )}
        <Button onClick={save} disabled={!selected || saving}
          data-testid="picker-save"
          className="bg-[#FF6B6B] hover:bg-[#FF5252] rounded-xl flex-1">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kaydediliyor…</> : "Hedef Olarak Belirle"}
        </Button>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-xs px-3 py-1.5 rounded-full border ${
        active ? "border-[#FF6B6B] bg-[#FF6B6B] text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"
      }`}>{children}</button>
  );
}
